import { Box, Paper, Typography, IconButton, Tooltip } from '@mui/material';
import { ContentCopy as ContentCopyIcon, Check as CheckIcon } from '@mui/icons-material';
import { useEffect, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Message } from '../../types';
import { useSoundEffects } from '../../hooks/useSoundEffects';

interface MessageBubbleProps {
  message: Message;
  playAudio?: boolean; // Control whether to play audio (default: false)
}

export function MessageBubble({ message, playAudio = false }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const { playSoundsFromText, stripSoundTags } = useSoundEffects();
  const [displayContent, setDisplayContent] = useState(message.content);
  const [copied, setCopied] = useState(false);
  const hasPlayedRef = useRef(false); // Track if audio has been played for this message

  // Handle copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  // Play sounds when assistant message appears (only if playAudio is true and not already played)
  useEffect(() => {
    if (!isUser && message.content) {
      // Always strip sound tags from display
      setDisplayContent(stripSoundTags(message.content));

      // Play sounds only if playAudio is true and hasn't been played yet
      if (playAudio && !hasPlayedRef.current) {
        hasPlayedRef.current = true;
        playSoundsFromText(message.content).catch((error) => {
          console.error('Error playing sounds:', error);
        });
      }
    } else {
      setDisplayContent(message.content);
    }
  }, [message.content, isUser, playAudio, playSoundsFromText, stripSoundTags]);

  // Reset played flag when message ID changes
  useEffect(() => {
    hasPlayedRef.current = false;
  }, [message.id]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: '95%',
          p: 2,
          borderRadius: 3,
          backgroundColor: isUser ? 'primary.main' : 'background.paper',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          border: isUser ? 'none' : 1,
          borderColor: 'divider',
          position: 'relative',
        }}
      >
        {/* Copy button */}
        <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
          <IconButton
            onClick={handleCopy}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 8,
              [isUser ? 'left' : 'right']: 8,
              opacity: 0.7,
              color: isUser ? 'rgba(0, 0, 0, 0.6)' : 'text.secondary',
              backgroundColor: isUser ? 'rgba(255, 255, 255, 0.3)' : 'transparent',
              '&:hover': {
                opacity: 1,
                backgroundColor: isUser ? 'rgba(255, 255, 255, 0.5)' : 'action.hover',
              },
            }}
          >
            {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Box
          sx={{
            '& p': { m: 0 },
            '& p + p': { mt: 1 },
            '& code': {
              backgroundColor: isUser ? 'rgba(255,255,255,0.2)' : 'action.hover',
              padding: '2px 6px',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875em',
            },
            '& pre': {
              backgroundColor: isUser ? 'rgba(255,255,255,0.1)' : 'action.hover',
              padding: 1.5,
              borderRadius: 2,
              overflow: 'auto',
              '& code': {
                backgroundColor: 'transparent',
                padding: 0,
              },
            },
          }}
        >
          <ReactMarkdown>{displayContent}</ReactMarkdown>
        </Box>

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            opacity: 0.7,
            textAlign: isUser ? 'right' : 'left',
          }}
        >
          {message.timestamp.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Typography>
      </Paper>
    </Box>
  );
}
